import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const THEME_CLASSES = {
    brand: "bg-brand-500 text-white",
    accent: "bg-accent-500 text-white",
    dark: "bg-ink-900 text-white",
};

function Item({ t }) {
    const showCta = t.cta_label && t.link;
    const isRoute = (t.link || "").startsWith("/") && !t.link?.startsWith("//");
    const inner = (
        <span className="inline-flex items-center gap-1.5 align-middle">
            <span>{t.message}</span>
            {showCta && (
                <span className="inline-flex items-center gap-1 font-semibold underline underline-offset-2 ml-1.5">
                    {t.cta_label} <ArrowRight className="h-3.5 w-3.5" />
                </span>
            )}
        </span>
    );
    if (!t.link) return <span className="px-8">{inner}</span>;
    if (isRoute) {
        return <Link to={t.link} className="px-8 hover:opacity-80 transition">{inner}</Link>;
    }
    const isExternal = /^https?:\/\//i.test(t.link);
    return (
        <a href={t.link} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noreferrer" : undefined} className="px-8 hover:opacity-80 transition">
            {inner}
        </a>
    );
}

export default function Ticker() {
    const [items, setItems] = useState([]);
    const [atTop, setAtTop] = useState(true);

    useEffect(() => {
        api.get("/tickers").then((r) => setItems(r.data || [])).catch(() => setItems([]));
    }, []);

    useEffect(() => {
        const onScroll = () => setAtTop(window.scrollY < 4);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (!items.length || !atTop) return null;
    // Group items by theme so adjacent same-theme items can share a strip.
    // Simpler: render one strip per ticker, but allow many to coexist as separate strips.
    // For best UX, combine all into a single marquee strip using the first ticker's theme.
    const themeClass = THEME_CLASSES[items[0].theme] || THEME_CLASSES.brand;
    // Duplicate the list so the marquee loops seamlessly.
    const sequence = [...items, ...items];
    // Slow down (or speed up) based on number of items to keep readability consistent.
    const duration = Math.max(20, items.length * 8);

    return (
        <div
            className={`${themeClass} overflow-hidden text-sm font-medium relative`}
            data-testid="ticker-bar"
            role="status"
            aria-live="polite"
        >
            <div
                className="ticker-track py-2 whitespace-nowrap"
                style={{ animationDuration: `${duration}s` }}
            >
                {sequence.map((t, i) => (
                    <Item key={`${t.id}-${i}`} t={t} />
                ))}
            </div>
        </div>
    );
}
